import * as React from 'react';
import type { TFunction } from '@mukil/i18n';
import {
  Button,
  DatePicker,
  Disclosure,
  FormField,
  MoneyInput,
  StyledSelect,
} from '@jumpstone/ui-react';
import type {
  EmployeeFormDraft,
  EmployeeFormErrors,
} from './employee_form_validation';
import { resolveCurrentEffectiveFrom } from './employee_form_validation';
import {
  COMPENSATION_REASONS,
  newHistoryRow,
  validateHistoryRows,
  type CompensationHistoryDraft,
  type HistoryDateError,
} from './employee_form_compensation_utils';
import type { CompensationReason } from '../../data/use_employee_compensation';
import { COMPENSATION_REASON_KEY } from '../../lib/label_keys';
import { useDatePickerLabels } from '../../lib/use_date_picker_labels';

const SELECT_TRIGGER_CLASSES =
  'nv-input flex h-9 w-full cursor-pointer items-center justify-between gap-2';

const HISTORY_DATE_ERROR_KEY: Record<HistoryDateError, string> = {
  required: 'employees.form_error_required',
  before_start: 'employees.form_error_effective_from_before_start',
  not_before: 'employees.form_error_history_date_not_before',
  duplicate: 'employees.form_error_history_date_duplicate',
};

export interface EmployeeFormCompensationFieldsProps {
  draft: EmployeeFormDraft;
  errors: EmployeeFormErrors;
  locale: string;
  t: TFunction;
  set: <K extends keyof EmployeeFormDraft>(
    key: K,
    value: EmployeeFormDraft[K],
  ) => void;
}

/**
 * CREATE-only part of the employee form: the date the salary above takes
 * effect, plus a collapsible repeater for earlier salary entries (so a
 * long-serving employee can be entered with their history). Editing pay on an
 * existing employee goes through the Change salary dialog instead. Pure
 * presentation -- draft state and validation live in
 * `employee_form_validation.ts` / `employee_form_compensation_utils.ts`.
 */
export function EmployeeFormCompensationFields({
  draft,
  errors,
  locale,
  t,
  set,
}: EmployeeFormCompensationFieldsProps): React.JSX.Element {
  const labels = useDatePickerLabels();
  const rows = draft.compensation_history;
  // Row errors only appear after a submit attempt flagged the repeater.
  const rowErrors =
    errors.compensation_history === undefined
      ? {}
      : validateHistoryRows(rows, {
          current_effective_from: resolveCurrentEffectiveFrom(draft),
          employment_start_date: draft.employment_start_date,
        });

  const reasonOptions = COMPENSATION_REASONS.map((reason) => ({
    value: reason,
    label: t(COMPENSATION_REASON_KEY[reason]),
  }));

  function updateRow(
    key: string,
    patch: Partial<Omit<CompensationHistoryDraft, 'key'>>,
  ): void {
    set(
      'compensation_history',
      rows.map((row) => (row.key === key ? { ...row, ...patch } : row)),
    );
  }

  return (
    <div className="sm:col-span-2 flex flex-col gap-3">
      <FormField
        label={t('employees.compensation_effective_from')}
        htmlFor="employee-form-compensation-effective-from"
        helpText={t('employees.compensation_effective_from_hint')}
        error={
          errors.compensation_effective_from &&
          t('employees.form_error_effective_from_before_start')
        }
      >
        <DatePicker
          id="employee-form-compensation-effective-from"
          value={draft.compensation_effective_from || null}
          locale={locale}
          labels={labels}
          aria-label={t('employees.compensation_effective_from')}
          error={Boolean(errors.compensation_effective_from)}
          onChange={(iso) => {
            set('compensation_effective_from', iso);
          }}
        />
      </FormField>

      {errors.compensation_history !== undefined && (
        <p className="nv-help is-error" role="alert">
          {t('employees.form_error_history_invalid')}
        </p>
      )}

      {/* A failed submit must never hide the rows that caused it. */}
      <Disclosure
        label={t('employees.compensation_history_title')}
        forceOpen={errors.compensation_history !== undefined}
        panelClassName="mt-3 flex flex-col gap-3"
      >
        {rows.map((row) => {
          // Rows without errors are absent from the map (see
          // validateHistoryRows), so this lookup can miss.
          const rowError = Object.hasOwn(rowErrors, row.key)
            ? rowErrors[row.key]
            : undefined;
          const dateId = `employee-form-compensation-${row.key}-date`;
          const amountId = `employee-form-compensation-${row.key}-amount`;
          return (
            <div
              key={row.key}
              className="grid grid-cols-1 gap-3 rounded-lg border border-[color:var(--line)] p-3 sm:grid-cols-2"
            >
              <FormField
                label={t('employees.compensation_col_effective_from')}
                htmlFor={dateId}
                error={
                  rowError?.date && t(HISTORY_DATE_ERROR_KEY[rowError.date])
                }
              >
                <DatePicker
                  id={dateId}
                  value={row.effective_from || null}
                  locale={locale}
                  labels={labels}
                  aria-label={t('employees.compensation_col_effective_from')}
                  error={Boolean(rowError?.date)}
                  onChange={(iso) => {
                    updateRow(row.key, { effective_from: iso });
                  }}
                />
              </FormField>
              <FormField
                label={t('employees.compensation_col_amount')}
                htmlFor={amountId}
                error={
                  rowError?.amount &&
                  t('employees.form_error_history_amount_required')
                }
              >
                <MoneyInput
                  id={amountId}
                  value={row.amount_cents}
                  blankZero
                  error={Boolean(rowError?.amount)}
                  onChange={(cents) => {
                    updateRow(row.key, { amount_cents: cents });
                  }}
                />
              </FormField>
              <FormField label={t('employees.compensation_reason')}>
                <StyledSelect<CompensationReason>
                  value={row.reason}
                  onChange={(reason) => {
                    updateRow(row.key, { reason });
                  }}
                  options={reasonOptions}
                  ariaLabel={t('employees.compensation_reason')}
                  triggerClassName={SELECT_TRIGGER_CLASSES}
                />
              </FormField>
              <div className="flex items-end">
                <Button
                  variant="ghost-danger"
                  size="sm"
                  onClick={() => {
                    set(
                      'compensation_history',
                      rows.filter((r) => r.key !== row.key),
                    );
                  }}
                >
                  {t('employees.compensation_history_remove')}
                </Button>
              </div>
            </div>
          );
        })}
        <div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              set('compensation_history', [...rows, newHistoryRow(rows)]);
            }}
          >
            {t('employees.compensation_history_add')}
          </Button>
        </div>
      </Disclosure>
    </div>
  );
}
